import * as React from 'react';
import { Modal, ModalHeader, ModalBody, Badge } from 'reactstrap';
import styled from 'styled-components';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { getGeneralSettings, getSettings } from '../../store/settings/selectors';
import { getMyUser } from '../../store/session/selectors';
import { alertError, alertGenericError } from '../../store/alerts/actions';
import { getErrorMessage } from '../../ajax/errors';
import {
    getCashRegisterEntriesAsync,
    handleCashRegisterPaymentsAsync,
    createCashRegisterReserveAsync,
    createCashRegisterWithdrawAsync,
    createCashRegisterTransferAsync,
    CashRegisterReferencePayload,
    CashRegisterCountReconciliation,
    getClientOrderByIdAsync,
    getPackAsync,
} from '../../ajax/requests';
import { CashRegisterEntryData, CashRegisterTransferDirection } from '../../models';
import { entryBelongsToScope, cashRegisterPaymentTypes, pendingBalanceOf } from '../../shared/appointmint/cashRegister';
import { openAppointment, openEntity } from '../../store/entities/actions';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import StyledTable from '../StyledTable';
import AppButton from '../AppButton';
import { NumberInputField } from '../Forms/fields/NumberInputField';
import { TextInputField } from '../Forms/fields/TextInputField';
import { ComboField } from '../Forms/fields/ComboField';
import { formatEuros } from '../../shared/string';
import { serviceName } from '../../shared/appointmint/appointment';

type ActionMode = 'process' | 'deposit' | 'withdraw' | 'transfer' | null;
type MovementFilter = 'all' | 'deposit' | 'withdraw' | 'transfer';
type StatusFilter = 'all' | 'pending' | 'handled';

const HISTORY_COL_COUNT = 5;

interface HistoryBatch {
    isPending: boolean;
    handledByUser?: string;
    handledAt?: string;
    total: number;
    entries: CashRegisterEntryData[];
}

const resolveUserName = (e: CashRegisterEntryData): string =>
    e.createdByUser ?? e.username ?? e.professionalName ?? '';

interface Props {
    // When embedded inside the shell modal the component renders body-only
    // (no own <Modal>/<ModalHeader>); the host owns the modal chrome.
    embedded?: boolean;
    isOpen?: boolean;
    register: { username?: string; cashRegisterName?: string };
    displayName: string;
    initialPaymentType?: string;
    canApprove: boolean;
    sharedRegisterNames: string[];
    onClose: () => void;
    onAfterAction?: () => void;
    onSwitchRegister?: () => void;
}

// Payment-type labels that represent physical cash — only these get the
// counted-cash reconciliation (bank-settled methods need no drawer count).
const CASH_LIKE = ['cash', 'dinheiro', 'numerário', 'numerario', 'espèces', 'especes', 'efectivo', 'efetivo', 'contado', 'contanti', 'bargeld', 'contante'];
const isCashLike = (type: string) => !!type && CASH_LIKE.some(c => type.toLowerCase().includes(c));

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-M);
`;

/* ---------- Summary card ---------- */

const SummaryCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-M);
    padding: var(--spacer-M);
    background: var(--bg-light);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: 0.5rem;
`;

const TypeGrid = styled.div`
    display: flex;
    gap: var(--spacer-S);
    flex-wrap: wrap;
`;

const TypeCell = styled.button<{ $active: boolean; type?: string; onClick?: React.MouseEventHandler<HTMLButtonElement>; children?: React.ReactNode }>`
    flex: 1;
    min-width: 8rem;
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XXS);
    padding: var(--spacer-S) var(--spacer-M);
    border-radius: 0.4rem;
    cursor: pointer;
    text-align: left;
    background: ${p => (p.$active ? 'var(--bg-hover-light)' : 'transparent')};
    border: var(--border-thin) solid ${p => (p.$active ? 'var(--info-color)' : 'var(--appLigthGrey)')};
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover {
        background: var(--bg-hover-light);
    }
`;

const TypeCellLabel = styled.span`
    font-size: 0.72rem;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const TypeCellValue = styled.strong<{ $negative: boolean; children?: React.ReactNode }>`
    font-size: 1.2rem;
    line-height: 1.1;
    color: ${p => (p.$negative ? 'var(--danger-color)' : 'var(--success-color)')};
`;

const Stats = styled.div`
    display: flex;
    gap: var(--spacer-L);
    flex-wrap: wrap;
`;

const Stat = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XXS);
`;

const StatLabel = styled.span`
    font-size: 0.72rem;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const StatValue = styled.strong<{ $negative?: boolean; children?: React.ReactNode }>`
    font-size: 2rem;
    line-height: 1.1;
    color: ${p => (p.$negative ? 'var(--danger-color)' : 'var(--success-color)')};
`;

const StatHint = styled.span`
    font-size: 0.7rem;
    color: var(--text-muted);
    max-width: 16rem;
`;

/* ---------- Actions ---------- */

const ActionsSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
`;

const ActionTabs = styled.div`
    display: flex;
    gap: var(--spacer-S);
    flex-wrap: nowrap;
`;

const ActionPanel = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
    padding: var(--spacer-M);
    background: var(--bg-light);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: 0.5rem;
`;

const ActionPanelFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: var(--spacer-XS);
`;

const InfoText = styled.span`
    color: var(--text-muted);
    font-size: smaller;
`;

const ErrorText = styled.span`
    color: var(--danger-color);
    font-size: smaller;
`;

const AmountRow = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--spacer-XS);
    padding: var(--spacer-S) var(--spacer-M);
    background: var(--bg-hover-light);
    border-radius: 0.4rem;
`;

const AmountRowLabel = styled.span`
    color: var(--text-muted);
    font-size: smaller;
`;

const ProcessAmount = styled.strong`
    font-size: 1.4rem;
`;

const DiscrepancyValue = styled.strong<{ $negative: boolean; $zero: boolean; children?: React.ReactNode }>`
    font-size: 1.4rem;
    color: ${p => (p.$zero ? 'var(--text-body)' : p.$negative ? 'var(--danger-color)' : 'var(--success-color)')};
`;

/* ---------- History ---------- */

const HistorySection = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
`;

const SectionTitle = styled.div`
    font-size: 0.78rem;
    font-weight: 700;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

const FiltersRow = styled.div`
    display: flex;
    gap: var(--spacer-S);
    flex-wrap: wrap;
`;

const FilterField = styled.div`
    min-width: 9rem;
    flex: 1;
`;

const HistoryScroll = styled.div`
    overflow: auto;
    max-height: 22rem;
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: 0.5rem;
`;

const DateCell = styled.td`
    padding-right: var(--spacer-M);
    white-space: nowrap;
    color: var(--text-muted);
    font-size: smaller;
`;

const OriginCell = styled.td`
    padding-right: var(--spacer-M);
    max-width: 22rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const AmountCell = styled.td<{ $negative: boolean; children?: React.ReactNode }>`
    padding-right: var(--spacer-M);
    white-space: nowrap;
    text-align: right;
    font-weight: 600;
    color: ${p => (p.$negative ? 'var(--danger-color)' : 'var(--success-color)')};
`;

const StatusCell = styled.td`
    white-space: nowrap;
`;

const UserCell = styled.td<{ title?: string; children?: React.ReactNode }>`
    padding-right: var(--spacer-M);
    white-space: nowrap;
    font-size: smaller;
    color: var(--text-muted);
    max-width: 12rem;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const HandledBy = styled.div<{ title?: string; children?: React.ReactNode }>`
    color: var(--text-muted);
    font-size: 0.72rem;
    max-width: 12rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const BatchHeaderRow = styled.tr`
    background: var(--bg-hover-light);
    border-top: var(--border-thin) solid var(--info-color);
    border-bottom: var(--border-thin) solid var(--info-color);

    td {
        padding: var(--spacer-XS) var(--spacer-M);
        font-size: 0.82rem;
    }
`;

const BatchHeaderLabel = styled.span`
    font-weight: 700;
    color: var(--info-color);
    margin-right: var(--spacer-S);
`;

const BatchHeaderDate = styled.span`
    color: var(--text-muted);
    margin-right: var(--spacer-S);
`;

const BatchHeaderTotal = styled.span`
    display: inline-block;
    padding: 0 var(--spacer-XS);
    border: var(--border-thin) solid var(--info-color);
    border-radius: 0.3rem;
    color: var(--info-color);
    font-weight: 600;
    font-size: 0.78rem;
`;

const PendingHeaderRow = styled.tr`
    background: var(--warning-bg);
    border-top: var(--border-thin) solid var(--warning-border);
    border-bottom: var(--border-thin) solid var(--warning-border);

    td {
        padding: var(--spacer-XS) var(--spacer-M);
        font-size: 0.82rem;
    }
`;

const PendingHeaderLabel = styled.span`
    font-weight: 700;
    color: var(--warning-color);
    margin-right: var(--spacer-S);
`;

const PendingHeaderTotal = styled.span`
    display: inline-block;
    padding: 0 var(--spacer-XS);
    border: var(--border-thin) solid var(--warning-color);
    border-radius: 0.3rem;
    color: var(--text-body);
    font-weight: 600;
    font-size: 0.78rem;
`;

const TabButton = styled(AppButton)`
    flex: 1;
    min-width: 0;
`;

const ModalTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-S);
`;

const headStyle: React.CSSProperties = { whiteSpace: 'nowrap' };
const headRight: React.CSSProperties = { ...headStyle, textAlign: 'right' };

const CashRegisterProcessModal: React.FC<Props> = ({
    embedded,
    isOpen,
    register,
    displayName,
    initialPaymentType,
    canApprove,
    sharedRegisterNames,
    onClose,
    onAfterAction,
    onSwitchRegister,
}) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const generalSettings = useSelector(getGeneralSettings);
    const settings = useSelector(getSettings);
    const currentUser = useSelector(getMyUser);
    const configuredCashType = settings.cashRegisterSettings?.cashPaymentType;

    const active = embedded ? true : !!isOpen;

    const [entries, setEntries] = React.useState<CashRegisterEntryData[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isBusy, setIsBusy] = React.useState(false);
    const [activeAction, setActiveAction] = React.useState<ActionMode>(null);
    const [selectedType, setSelectedType] = React.useState<string>('');

    // History filters
    const [periodDays, setPeriodDays] = React.useState(30);
    const [movementFilter, setMovementFilter] = React.useState<MovementFilter>('all');
    const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('all');

    // Action inputs
    const [amount, setAmount] = React.useState<number | undefined>(undefined);
    const [description, setDescription] = React.useState('');
    const [reserveAmount, setReserveAmount] = React.useState<number | undefined>(0);
    const [transferDestination, setTransferDestination] = React.useState('');
    const [countedCash, setCountedCash] = React.useState<number | undefined>(undefined);

    const isShared = !!register.cashRegisterName;
    const transferDestinations = isShared
        ? sharedRegisterNames.filter(n => n !== register.cashRegisterName)
        : sharedRegisterNames;

    const loadEntries = React.useCallback(() => {
        const from = moment().subtract(periodDays, 'days').startOf('day').format('YYYY-MM-DDTHH:mm:ss');
        const to = moment().endOf('day').format('YYYY-MM-DDTHH:mm:ss');
        setIsLoading(true);
        return getCashRegisterEntriesAsync(from, to)
            .then(all => setEntries(all.filter(e => entryBelongsToScope(e, register))))
            .catch(() => setEntries([]))
            .finally(() => setIsLoading(false));
    }, [periodDays, register.cashRegisterName, register.username]);

    React.useEffect(() => {
        if (!active) return;
        setActiveAction(null);
        loadEntries();
    }, [active, loadEntries]);

    // Distinct payment types seen on this register, unioned with the configured
    // methods so every method stays selectable (Zappy "Vendas" breakdown).
    const paymentTypes = React.useMemo(
        () => cashRegisterPaymentTypes(entries, generalSettings.paymentMethods),
        [entries, generalSettings.paymentMethods],
    );

    // Keep a valid selected type: prefer the requested initial type.
    React.useEffect(() => {
        if (paymentTypes.length === 0) return;
        setSelectedType(prev => {
            if (prev && paymentTypes.includes(prev)) return prev;
            if (initialPaymentType && paymentTypes.includes(initialPaymentType)) return initialPaymentType;
            return paymentTypes[0];
        });
    }, [paymentTypes, initialPaymentType]);

    const balanceOf = React.useCallback(
        (type: string) => pendingBalanceOf(entries, type),
        [entries]);

    // Balance for the selected payment type: money still physically in the
    // register, i.e. every entry not yet processed.
    const balance = React.useMemo(() => balanceOf(selectedType), [balanceOf, selectedType]);

    const pendingForType = React.useMemo(() =>
        entries.filter(e => e.paymentType === selectedType && !e.handledByUser),
        [entries, selectedType]);

    const movementOf = (e: CashRegisterEntryData): MovementFilter | 'sale' => {
        if (e.transferId) return 'transfer';
        if (e.appointmentId || e.packId || e.orderId) return 'sale';
        return e.paymentValue < 0 ? 'withdraw' : 'deposit';
    };

    const visibleHistory = React.useMemo(() => entries
        .filter(e => e.paymentType === selectedType)
        .filter(e => movementFilter === 'all' || movementOf(e) === movementFilter)
        .filter(e => statusFilter === 'all'
            || (statusFilter === 'pending' && !e.handledByUser)
            || (statusFilter === 'handled' && !!e.handledByUser))
        .sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime()),
        [entries, selectedType, movementFilter, statusFilter]);

    const historyBatches = React.useMemo((): HistoryBatch[] => {
        const batchMap = new Map<string, HistoryBatch>();
        for (const e of visibleHistory) {
            const key = e.handledByUser && e.handledAt
                ? `${e.handledByUser}||${e.handledAt}`
                : '__pending__';
            if (!batchMap.has(key)) {
                batchMap.set(key, {
                    isPending: key === '__pending__',
                    handledByUser: e.handledByUser ?? undefined,
                    handledAt: e.handledAt ?? undefined,
                    total: 0,
                    entries: [],
                });
            }
            const batch = batchMap.get(key)!;
            batch.entries.push(e);
            batch.total += e.paymentValue;
        }
        const latestDate = (b: HistoryBatch) =>
            Math.max(...b.entries.map(e => new Date(e.dateTime).getTime()));
        return [...batchMap.values()].sort((a, b) => latestDate(b) - latestDate(a));
    }, [visibleHistory]);

    const openOrigin = (e: CashRegisterEntryData) => {
        if (e.appointmentId) dispatch(openAppointment(e.appointmentId));
        else if (e.packId) dispatch(openEntity('clientPacks', e.packId, () => getPackAsync(e.packId!)));
        else if (e.orderId) dispatch(openEntity('orders', e.orderId, () => getClientOrderByIdAsync(e.orderId!)));
    };

    const renderOrigin = (e: CashRegisterEntryData) => {
        if (e.transferId) {
            const name = e.transferCounterpartyName ?? '';
            return <em>{e.transferDirection === CashRegisterTransferDirection.Out
                ? t(TK.cashRegisterTransferTo).replace('{0}', name)
                : t(TK.cashRegisterTransferFrom).replace('{0}', name)}</em>;
        }
        if (e.appointmentId || e.packId || e.orderId) {
            const label = e.appointmentId
                ? `${e.clientName} (${e.appointmentType ? serviceName(e.appointmentType, generalSettings) : '-'})`
                : e.packId ? `${e.clientName} (${e.packName})` : `${e.clientName} (${t(TK.orders)})`;
            return (
                <AppButton color="link" size="sm" style={{ padding: 0 }} onClick={() => openOrigin(e)}>
                    {label}
                </AppButton>
            );
        }
        return <em>{e.description || '-'}</em>;
    };

    const toggleAction = (mode: Exclude<ActionMode, null>) => {
        setActiveAction(prev => (prev === mode ? null : mode));
        setAmount(mode === 'transfer' && balance > 0 ? balance : undefined);
        setReserveAmount(0);
        setCountedCash(undefined);
        setTransferDestination(transferDestinations[0] ?? '');
        setDescription(mode === 'deposit' ? t(TK.cashRegisterDeposit) : mode === 'process' ? t(TK.cashRegisterReserveLabel) : '');
    };

    const selectType = (type: string) => {
        setSelectedType(type);
        setActiveAction(null);
        setCountedCash(undefined);
    };

    const reload = async () => {
        await loadEntries();
        setActiveAction(null);
        setCountedCash(undefined);
        onAfterAction?.();
    };

    const runAction = async (fn: () => Promise<unknown>) => {
        setIsBusy(true);
        try {
            await fn();
            await reload();
        } catch (err) {
            const message = getErrorMessage(err);
            dispatch(message ? alertError(message) : alertGenericError());
        } finally {
            setIsBusy(false);
        }
    };

    const targetUsername = isShared ? undefined : register.username;

    const handleDeposit = () => runAction(() =>
        createCashRegisterReserveAsync(selectedType, amount ?? 0, description, register.cashRegisterName));

    const handleWithdraw = () => runAction(() =>
        createCashRegisterWithdrawAsync(selectedType, amount ?? 0, description, register.cashRegisterName, targetUsername));

    const handleTransfer = () => runAction(() => {
        const source: CashRegisterReferencePayload = isShared
            ? { cashRegisterName: register.cashRegisterName }
            : { username: register.username };
        return createCashRegisterTransferAsync(selectedType, amount ?? 0, source, { cashRegisterName: transferDestination }, description);
    });

    const showCount = configuredCashType
        ? selectedType === configuredCashType
        : isCashLike(selectedType);
    const counted = countedCash ?? 0;
    const discrepancy = counted - balance;

    const handleProcess = () => runAction(async () => {
        const ids = pendingForType.map(e => e.id);
        const reserve = reserveAmount ?? 0;
        const reconciliation: CashRegisterCountReconciliation | undefined =
            showCount && countedCash !== undefined
                ? {
                    countedAmount: countedCash,
                    paymentType: selectedType,
                    cashRegisterName: register.cashRegisterName,
                    targetUsername: isShared ? undefined : register.username,
                }
                : undefined;
        if (ids.length === 0 && isShared) {
            const net = balance - reserve;
            if (net > 0) await createCashRegisterWithdrawAsync(selectedType, net, '', register.cashRegisterName, undefined);
            return;
        }
        if (ids.length > 0) await handleCashRegisterPaymentsAsync(ids, reconciliation);
        if (reserve > 0) await createCashRegisterReserveAsync(selectedType, reserve, description, register.cashRegisterName);
    });

    const amt = amount ?? 0;
    const withdrawGoesNegative = activeAction === 'withdraw' && amt > balance;
    const transferExceedsBalance = activeAction === 'transfer' && amt > balance;

    const tabBtn = (
        mode: Exclude<ActionMode, null>,
        label: string,
        icon: React.ReactNode,
        activeColor: 'primary' | 'success' | 'danger' = 'primary',
    ) => (
        <TabButton
            color={activeAction === mode ? activeColor : 'secondary'}
            outline={activeAction !== mode}
            startIcon={icon}
            onClick={() => toggleAction(mode)}
        >
            {label}
        </TabButton>
    );

    const body = (
        <Body>
            <SummaryCard>
                {paymentTypes.length > 1 && (
                    <TypeGrid>
                        {paymentTypes.map(type => {
                            const tb = balanceOf(type);
                            return (
                                <TypeCell
                                    key={type}
                                    type="button"
                                    $active={type === selectedType}
                                    onClick={() => selectType(type)}
                                >
                                    <TypeCellLabel>{type}</TypeCellLabel>
                                    <TypeCellValue $negative={tb < 0}>{formatEuros(tb)}</TypeCellValue>
                                </TypeCell>
                            );
                        })}
                    </TypeGrid>
                )}
                <Stats>
                    <Stat>
                        <StatLabel>{t(TK.cashRegisterBalanceLabel)}</StatLabel>
                        <StatValue $negative={balance < 0}>{formatEuros(balance)}</StatValue>
                        <StatHint>{t(TK.cashRegisterBalanceHelp)}</StatHint>
                    </Stat>
                </Stats>
            </SummaryCard>

            {canApprove && selectedType && (
                <ActionsSection>
                    <ActionTabs>
                        {tabBtn('process', t(TK.cashRegisterProcessRegister), <DoneAllIcon fontSize="small" />)}
                        {tabBtn('deposit', t(TK.cashRegisterDeposit), <AddCircleOutlineIcon fontSize="small" />, 'success')}
                        {tabBtn('withdraw', t(TK.cashRegisterWithdraw), <RemoveCircleOutlineIcon fontSize="small" />, 'danger')}
                        {transferDestinations.length > 0 && tabBtn('transfer', t(TK.cashRegisterTransfer), <SwapHorizIcon fontSize="small" />)}
                    </ActionTabs>

                    {activeAction && (
                        <ActionPanel>
                            {activeAction === 'deposit' && (
                                <>
                                    <NumberInputField label={t(TK.cashRegisterDepositAmount)} value={amount} min={0.01} step={0.01} onChange={setAmount} />
                                    <TextInputField label={t(TK.cashRegisterReserveDescription)} value={description} onChange={setDescription} />
                                    <ActionPanelFooter>
                                        <AppButton color="success" disabled={isBusy || amt <= 0} onClick={handleDeposit}>{t(TK.confirm)}</AppButton>
                                    </ActionPanelFooter>
                                </>
                            )}

                            {activeAction === 'withdraw' && (
                                <>
                                    <NumberInputField label={t(TK.cashRegisterWithdrawAmount)} value={amount} min={0.01} step={0.01} onChange={setAmount} />
                                    {withdrawGoesNegative && <InfoText>{t(TK.cashRegisterWithdrawCanGoNegative)}</InfoText>}
                                    <TextInputField label={t(TK.cashRegisterReserveDescription)} value={description} onChange={setDescription} />
                                    <ActionPanelFooter>
                                        <AppButton color="danger" disabled={isBusy || amt <= 0} onClick={handleWithdraw}>{t(TK.confirm)}</AppButton>
                                    </ActionPanelFooter>
                                </>
                            )}

                            {activeAction === 'transfer' && (
                                <>
                                    <ComboField
                                        label={t(TK.cashRegisterTransferDestination)}
                                        value={transferDestination}
                                        options={transferDestinations.map(n => ({ value: n, label: n }))}
                                        onChange={v => setTransferDestination(v ?? '')}
                                    />
                                    <NumberInputField label={t(TK.cashRegisterTransferAmount)} value={amount} min={0.01} max={balance} step={0.01} onChange={setAmount} />
                                    {transferExceedsBalance && <ErrorText>{t(TK.cashRegisterTransferExceedsTotal)}</ErrorText>}
                                    <TextInputField label={t(TK.cashRegisterTransferDescription)} value={description} onChange={setDescription} />
                                    <ActionPanelFooter>
                                        <AppButton color="primary" disabled={isBusy || amt <= 0 || transferExceedsBalance || !transferDestination} onClick={handleTransfer}>{t(TK.confirm)}</AppButton>
                                    </ActionPanelFooter>
                                </>
                            )}

                            {activeAction === 'process' && (
                                <>
                                    <InfoText>{t(TK.cashRegisterProcessDescription)}</InfoText>
                                    <AmountRow>
                                        <AmountRowLabel>{t(TK.cashRegisterTotal)} ({selectedType})</AmountRowLabel>
                                        <ProcessAmount>{formatEuros(balance)}</ProcessAmount>
                                    </AmountRow>
                                    {showCount && (
                                        <>
                                            <NumberInputField
                                                label={t(TK.cashRegisterCountedCash)}
                                                helpText={t(TK.cashRegisterCountedCashHelp)}
                                                value={countedCash}
                                                min={0}
                                                step={0.01}
                                                onChange={setCountedCash}
                                            />
                                            {countedCash !== undefined && (
                                                <>
                                                    <AmountRow>
                                                        <AmountRowLabel>{t(TK.cashRegisterExpectedCash)}</AmountRowLabel>
                                                        <ProcessAmount>{formatEuros(balance)}</ProcessAmount>
                                                    </AmountRow>
                                                    <AmountRow>
                                                        <AmountRowLabel>
                                                            {t(TK.cashRegisterDiscrepancy)}
                                                            {discrepancy !== 0 && ` (${discrepancy < 0 ? t(TK.cashRegisterShort) : t(TK.cashRegisterOver)})`}
                                                        </AmountRowLabel>
                                                        <DiscrepancyValue $negative={discrepancy < 0} $zero={discrepancy === 0}>
                                                            {formatEuros(discrepancy)}
                                                        </DiscrepancyValue>
                                                    </AmountRow>
                                                </>
                                            )}
                                        </>
                                    )}
                                    <NumberInputField label={t(TK.cashRegisterReserveAmount)} value={reserveAmount} min={0} step={0.01} onChange={setReserveAmount} />
                                    {(reserveAmount ?? 0) > 0 && (
                                        <TextInputField label={t(TK.cashRegisterReserveDescription)} value={description} onChange={setDescription} />
                                    )}
                                    <ActionPanelFooter>
                                        <AppButton
                                            color="primary"
                                            disabled={isBusy || (pendingForType.length === 0 && !(isShared && balance > 0))}
                                            onClick={handleProcess}
                                        >
                                            {t(TK.confirm)}
                                        </AppButton>
                                    </ActionPanelFooter>
                                </>
                            )}
                        </ActionPanel>
                    )}
                </ActionsSection>
            )}

            <HistorySection>
                <SectionTitle>{t(TK.cashRegisterHistory)}</SectionTitle>
                <FiltersRow>
                    <FilterField>
                        <ComboField
                            label={t(TK.cashRegisterPaymentType)}
                            value={selectedType}
                            options={paymentTypes.map(type => ({ value: type, label: type }))}
                            onChange={v => v && selectType(v)}
                        />
                    </FilterField>
                    <FilterField>
                        <ComboField
                            label={t(TK.cashRegisterPeriod)}
                            value={String(periodDays)}
                            options={[7, 30, 90].map(d => ({ value: String(d), label: `${d} ${t(TK.cashRegisterDays)}` }))}
                            onChange={v => setPeriodDays(Number(v) || 30)}
                        />
                    </FilterField>
                    <FilterField>
                        <ComboField
                            label={t(TK.cashRegisterMovement)}
                            value={movementFilter}
                            options={[
                                { value: 'all', label: t(TK.all) },
                                { value: 'deposit', label: t(TK.cashRegisterDeposit) },
                                { value: 'withdraw', label: t(TK.cashRegisterWithdraw) },
                                { value: 'transfer', label: t(TK.cashRegisterTransfer) },
                            ]}
                            onChange={v => setMovementFilter((v as MovementFilter) ?? 'all')}
                        />
                    </FilterField>
                    <FilterField>
                        <ComboField
                            label={t(TK.status)}
                            value={statusFilter}
                            options={[
                                { value: 'all', label: t(TK.all) },
                                { value: 'pending', label: t(TK.cashRegisterToProcess) },
                                { value: 'handled', label: t(TK.cashRegisterHandled) },
                            ]}
                            onChange={v => setStatusFilter((v as StatusFilter) ?? 'all')}
                        />
                    </FilterField>
                </FiltersRow>

                <HistoryScroll>
                    {isLoading ? (
                        <div style={{ padding: 'var(--spacer-M)' }}><InfoText>{t(TK.loading)}...</InfoText></div>
                    ) : visibleHistory.length === 0 ? (
                        <div style={{ padding: 'var(--spacer-M)' }}><InfoText>{t(TK.cashRegisterNoEntries)}</InfoText></div>
                    ) : (
                        <StyledTable hover>
                            <thead>
                                <tr>
                                    <th style={headStyle}>{t(TK.date)}</th>
                                    <th style={headStyle}>{t(TK.source)}</th>
                                    <th style={headRight}>{t(TK.value)}</th>
                                    <th style={headStyle}>{t(TK.user)}</th>
                                    <th style={headStyle}>{t(TK.status)}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {historyBatches.map(batch => (
                                    <React.Fragment key={batch.isPending ? '__pending__' : `${batch.handledByUser}||${batch.handledAt}`}>
                                        {batch.isPending ? (
                                            <PendingHeaderRow>
                                                <td colSpan={HISTORY_COL_COUNT}>
                                                    <PendingHeaderLabel>{t(TK.cashRegisterPending)}</PendingHeaderLabel>
                                                    <PendingHeaderTotal>{selectedType}: {formatEuros(batch.total)}</PendingHeaderTotal>
                                                </td>
                                            </PendingHeaderRow>
                                        ) : (
                                            <BatchHeaderRow>
                                                <td colSpan={HISTORY_COL_COUNT}>
                                                    <BatchHeaderLabel>{t(TK.cashRegisterHandledBy)}</BatchHeaderLabel>
                                                    <BatchHeaderDate>{batch.handledByUser}</BatchHeaderDate>
                                                    <BatchHeaderDate>{moment(batch.handledAt).format('YYYY-MM-DD HH:mm')}</BatchHeaderDate>
                                                    <BatchHeaderTotal>{selectedType}: {formatEuros(batch.total)}</BatchHeaderTotal>
                                                </td>
                                            </BatchHeaderRow>
                                        )}
                                        {batch.entries.map(e => (
                                            <tr key={e.id}>
                                                <DateCell>{moment(e.dateTime).format('YYYY-MM-DD HH:mm')}</DateCell>
                                                <OriginCell>{renderOrigin(e)}</OriginCell>
                                                <AmountCell $negative={e.paymentValue < 0}>{formatEuros(e.paymentValue)}</AmountCell>
                                                <UserCell title={resolveUserName(e)}>{resolveUserName(e)}</UserCell>
                                                <StatusCell>
                                                    {e.handledByUser ? (
                                                        <Badge color="success">{t(TK.cashRegisterHandled)}</Badge>
                                                    ) : (
                                                        <Badge color="warning">{t(TK.cashRegisterToProcess)}</Badge>
                                                    )}
                                                </StatusCell>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </StyledTable>
                    )}
                </HistoryScroll>
            </HistorySection>
        </Body>
    );

    if (embedded) return <ModalBody>{body}</ModalBody>;

    return (
        <Modal isOpen={active} toggle={onClose} size="lg">
            <ModalHeader toggle={onClose}>
                <ModalTitleRow>
                    <span>{displayName}</span>
                    {onSwitchRegister && sharedRegisterNames.length > 0 && (
                        <AppButton color="secondary" outline size="sm" startIcon={<SwapHorizIcon fontSize="small" />} onClick={onSwitchRegister}>
                            {t(TK.cashRegisterChangeRegister)}
                        </AppButton>
                    )}
                </ModalTitleRow>
            </ModalHeader>
            <ModalBody>{body}</ModalBody>
        </Modal>
    );
};

export default CashRegisterProcessModal;
